import { HStack, Table, VStack } from '@chakra-ui/react';
import type { SkeletonProps } from '@chakra-ui/react';
import React from 'react';
import type { CSSProperties } from 'react';
import { Skeleton, SkeletonText } from '@/components/ui/skeleton';
import { TableScrollArea, stickyColumnProps, stickyEndColumnProps } from './TableScrollArea';

type TableSkeletonColumnType = 'text' | 'date';
type TableSkeletonColumn = { width: string; type?: TableSkeletonColumnType };

interface TableSkeletonProps {
  rows?: number;
  columns?: TableSkeletonColumn[];
  stickyColumns?: 0 | 1 | 2;
  stickyLastColumn?: boolean;
  firstColumnWidth?: string;
  secondColumnWidth?: string;
  lastColumnWidth?: string;
  scrollAreaStyle?: CSSProperties;
}

const DEFAULT_COLUMNS: TableSkeletonColumn[] = [
  { width: '180px' },
  { width: '140px' },
  { width: '120px' },
  { width: '120px' },
];

const isSelectionColumn = (columnWidth: string) =>
  ['40px', '48px', '50px'].includes(columnWidth) || columnWidth.includes('--table-bleed-x');

const getPixelWidth = (columnWidth: string) => Number(columnWidth.replace('px', ''));

const isActionsColumn = (columnWidth: string, colIndex: number, columnCount: number) =>
  colIndex === columnCount - 1 && getPixelWidth(columnWidth) <= 120;

/** Every placeholder block: the table's warm skeleton colour on a 4px radius. */
const SKELETON_BLOCK_STYLE = { bg: 'skeletonBg', rounded: 'xs' } as const;

const SkeletonBlock = (props: SkeletonProps) => <Skeleton {...SKELETON_BLOCK_STYLE} {...props} />;

const HeaderSkeleton = ({ width }: { width: string }) => (
  <SkeletonBlock height="12px" width={width} maxW="full" opacity={0.55} />
);

const DateCellSkeleton = () => (
  <VStack align="flex-start" gap={1.5}>
    <SkeletonBlock height="14px" width="82px" />
    <SkeletonBlock height="10px" width="56px" opacity={0.7} />
  </VStack>
);

// Stands in for the 32px row action buttons, so it keeps their 8px radius.
const ActionsCellSkeleton = () => (
  <HStack gap={1.5} justify="flex-end">
    <SkeletonBlock boxSize="32px" rounded="md" />
    <SkeletonBlock boxSize="32px" rounded="md" />
  </HStack>
);

const getStickyProps = (
  colIndex: number,
  stickyColumns: 0 | 1 | 2,
  stickyLastColumn: boolean,
  columns: NonNullable<TableSkeletonProps['columns']>,
  firstColumnWidth?: string,
  secondColumnWidth?: string,
  lastColumnWidth?: string
) => {
  if (stickyLastColumn && colIndex === columns.length - 1) {
    return stickyEndColumnProps(lastColumnWidth ?? columns[columns.length - 1]?.width ?? '0px');
  }

  if (stickyColumns === 0) return {};

  const firstWidth = firstColumnWidth ?? columns[0]?.width ?? '0px';
  if (colIndex === 0) {
    return stickyColumnProps(firstWidth, '0px', stickyColumns > 1 ? { divider: 'end' } : undefined);
  }

  if (stickyColumns > 1 && colIndex === 1) {
    return stickyColumnProps(secondColumnWidth ?? columns[1]?.width ?? '0px', firstWidth);
  }

  return {};
};

const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 5,
  columns = DEFAULT_COLUMNS,
  stickyColumns = 0,
  stickyLastColumn = false,
  firstColumnWidth,
  secondColumnWidth,
  lastColumnWidth,
  scrollAreaStyle,
}) => {
  return (
    <TableScrollArea style={scrollAreaStyle}>
      <Table.Root overflow="visible" aria-busy="true">
        <Table.Header>
          <Table.Row>
            {columns.map((column, colIndex) => {
              const stickyProps = getStickyProps(
                colIndex,
                stickyColumns,
                stickyLastColumn,
                columns,
                firstColumnWidth,
                secondColumnWidth,
                lastColumnWidth
              );

              return (
                <Table.ColumnHeader
                  key={`header-${colIndex}`}
                  width={column.width}
                  textAlign={colIndex === columns.length - 1 ? 'end' : undefined}
                  minW={colIndex === columns.length - 1 ? column.width : undefined}
                  {...stickyProps}
                >
                  {isSelectionColumn(column.width) ? (
                    <SkeletonBlock boxSize="18px" opacity={0.55} />
                  ) : (
                    <HeaderSkeleton width={column.width} />
                  )}
                </Table.ColumnHeader>
              );
            })}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <Table.Row key={`row-${rowIndex}`}>
              {columns.map((column, colIndex) => {
                const isSelection = isSelectionColumn(column.width);
                const isActions = isActionsColumn(column.width, colIndex, columns.length);
                const stickyProps = getStickyProps(
                  colIndex,
                  stickyColumns,
                  stickyLastColumn,
                  columns,
                  firstColumnWidth,
                  secondColumnWidth,
                  lastColumnWidth
                );

                return (
                  <Table.Cell
                    key={`cell-${rowIndex}-${colIndex}`}
                    width={column.width}
                    textAlign={colIndex === columns.length - 1 ? 'end' : undefined}
                    minW={colIndex === columns.length - 1 ? column.width : undefined}
                    {...stickyProps}
                  >
                    {isSelection && <SkeletonBlock boxSize="18px" />}
                    {!isSelection && column.type === 'date' && <DateCellSkeleton />}
                    {!isSelection && column.type !== 'date' && isActions && <ActionsCellSkeleton />}
                    {!isSelection && column.type !== 'date' && !isActions && (
                      <SkeletonText
                        noOfLines={1}
                        height="4"
                        w={column.width}
                        maxW="full"
                        {...SKELETON_BLOCK_STYLE}
                      />
                    )}
                  </Table.Cell>
                );
              })}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </TableScrollArea>
  );
};

export default TableSkeleton;
